/**
 * QuickPlan — simplified destinations+nights input and rich explore panels.
 *
 * Used by ItineraryBuilder in "plan" and "explore" modes:
 *   - QuickPlanForm:   one-form input (destinations, nights each, style, interests, preferred modes)
 *   - StayExplorePanel: slide-in side panel (Sights / Lodging / Restaurants tabs) for a selected stay
 *   - TransitExplorePanel: slide-in side panel for a selected transit leg (mode switcher + along-route sights)
 *   - ExploreBottomStrip: horizontal day-by-day strip with check-in times and drive blocks
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  PlusIcon, TrashIcon, MapPinIcon, SparklesIcon, ArrowPathIcon,
  XMarkIcon, LinkIcon, ArrowRightIcon, GlobeAltIcon,
} from '@heroicons/react/24/outline';
import type {
  Stay, TransitSegment, ItinLocation, RouteSight, TransitMode,
  LodgingRec, DiningRec, ItinActivity,
} from '../../services/api';
import { itineraryV2API } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

export interface QuickPlanDestination {
  tempId: string;
  city: string;
  country: string;
  lat?: number;
  lon?: number;
  nights: number;
}

export interface QuickPlanRequest {
  title: string;
  start_date: string; // YYYY-MM-DD
  travel_style: string;
  budget_level: string;
  group_type: string;
  interests: string[];
  destinations: QuickPlanDestination[];
  allowed_modes: TransitMode[];
  default_mode: TransitMode;
  nationality?: string;
}

const TRAVEL_STYLES = ['relaxed', 'moderate', 'packed'] as const;
const BUDGET_LEVELS = ['budget', 'midrange', 'luxury'] as const;
const GROUP_TYPES = ['solo', 'couple', 'family', 'group'] as const;
const INTERESTS = [
  'history', 'food', 'nature', 'beaches', 'art',
  'adventure', 'nightlife', 'shopping', 'architecture', 'wildlife',
];
const TRANSIT_MODE_OPTIONS: { value: TransitMode; label: string; icon: string }[] = [
  { value: 'drive', label: 'Drive',  icon: '🚗' },
  { value: 'train', label: 'Train',  icon: '🚆' },
  { value: 'ferry', label: 'Ferry',  icon: '⛴️' },
  { value: 'bus',   label: 'Bus',    icon: '🚌' },
  { value: 'fly',   label: 'Fly',    icon: '✈️' },
];

function genTempId() {
  return Math.random().toString(36).slice(2, 10);
}

function makeEmptyDestination(): QuickPlanDestination {
  return { tempId: genTempId(), city: '', country: '', nights: 2 };
}

// ─────────────────────────────────────────────────────────────────────────────
// Location autocomplete (smaller, inlined to keep QuickPlan self-contained)
// ─────────────────────────────────────────────────────────────────────────────

interface DestRow {
  city: string; country: string; lat?: number; lon?: number;
}

const LocationField: React.FC<{
  value: { city: string; country: string; lat?: number; lon?: number };
  onChange: (loc: { city: string; country: string; lat?: number; lon?: number }) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = 'City, Country' }) => {
  const [query, setQuery] = useState(
    value.city ? `${value.city}${value.country ? ', ' + value.country : ''}` : ''
  );
  const [results, setResults] = useState<DestRow[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value.city && !query) {
      setQuery(`${value.city}${value.country ? ', ' + value.country : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.city]);

  const searchKnown = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const resp = await fetch(`/api/destinations/search?q=${encodeURIComponent(q)}&limit=6`);
      if (resp.ok) {
        const data = await resp.json();
        setResults((data.destinations || []).map((d: any) => ({
          city: d.city, country: d.country, lat: d.lat, lon: d.lon,
        })));
      }
    } catch { setResults([]); }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);

    // Eagerly commit parsed text to parent state so a submit-without-blur
    // (clicking Generate mid-edit) still captures the user's latest input.
    // Coords are cleared here; the orchestrator re-geocodes on submit.
    const parts = v.split(',').map(p => p.trim());
    const city = parts[0] || '';
    const country = parts.slice(1).join(', ');
    if (city !== value.city || country !== value.country) {
      onChange({ city, country, lat: undefined, lon: undefined });
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchKnown(v), 250);
  };

  const select = (r: DestRow) => {
    setQuery(`${r.city}, ${r.country}`);
    onChange({ city: r.city, country: r.country, lat: r.lat, lon: r.lon });
    setOpen(false);
    setResults([]);
  };

  const geocodeFree = async (raw: string) => {
    const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) {
      // single token: commit city only, clear stale coords so orchestrator re-geocodes
      onChange({ city: parts[0] || '', country: '', lat: undefined, lon: undefined });
      setOpen(false);
      return;
    }
    const city = parts[0];
    const country = parts.slice(1).join(', ');
    try {
      const r = await itineraryV2API.geocode(city, country);
      onChange({ city, country, lat: r.lat, lon: r.lon });
    } catch {
      // Geocode failed — commit name but CLEAR stale coords (old value may have
      // been wrong) so the orchestrator tries again when the user hits Generate.
      onChange({ city, country, lat: undefined, lon: undefined });
    }
    setQuery(`${city}, ${country}`);
    setOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
    const trimmed = query.trim();
    if (!trimmed) return;
    // Re-commit whenever what the user typed differs from the currently committed
    // value — even if value.city was already populated. This covers the case where
    // someone edits a pre-filled destination (e.g. fixes a spelling) and blurs
    // without selecting from the dropdown.
    const currentDisplay = value.city
      ? `${value.city}${value.country ? ', ' + value.country : ''}`
      : '';
    if (trimmed === currentDisplay) return;
    geocodeFree(trimmed);
  };

  return (
    <div className="relative flex-1 min-w-0">
      <div className="relative">
        <MapPinIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onBlur={handleBlur}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      </div>
      {open && (results.length > 0 || query.includes(',')) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {results.map((r, i) => (
            <button key={i} type="button" onMouseDown={() => select(r)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-left">
              <MapPinIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
              <span>{r.city}, {r.country}</span>
            </button>
          ))}
          {query.includes(',') && (
            <button type="button" onMouseDown={() => geocodeFree(query)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 border-t border-gray-100 dark:border-gray-700">
              <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Use "{query}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QuickPlanForm — one-form input
// ─────────────────────────────────────────────────────────────────────────────

export interface QuickPlanFormProps {
  initial?: Partial<QuickPlanRequest>;
  onGenerate: (req: QuickPlanRequest) => void;
  isGenerating: boolean;
  progressMessage?: string | null;
  errorMessage?: string | null;
  onSwitchToAdvanced?: () => void;
}

export const QuickPlanForm: React.FC<QuickPlanFormProps> = ({
  initial, onGenerate, isGenerating, progressMessage, errorMessage, onSwitchToAdvanced,
}) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [startDate, setStartDate] = useState(initial?.start_date || '');
  const [travelStyle, setTravelStyle] = useState(initial?.travel_style || 'moderate');
  const [budgetLevel, setBudgetLevel] = useState(initial?.budget_level || 'midrange');
  const [groupType, setGroupType] = useState(initial?.group_type || 'couple');
  const [interests, setInterests] = useState<string[]>(initial?.interests || []);
  const [destinations, setDestinations] = useState<QuickPlanDestination[]>(
    initial?.destinations && initial.destinations.length > 0
      ? initial.destinations
      : [makeEmptyDestination(), makeEmptyDestination()]
  );
  const [allowedModes, setAllowedModes] = useState<TransitMode[]>(
    initial?.allowed_modes || ['drive', 'ferry', 'train', 'fly']
  );
  const [defaultMode, setDefaultMode] = useState<TransitMode>(initial?.default_mode || 'drive');

  const toggleInterest = (i: string) =>
    setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);

  const toggleMode = (m: TransitMode) =>
    setAllowedModes(prev => {
      const next = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m];
      return next.length === 0 ? prev : next; // keep at least one
    });

  const updateDest = (tempId: string, patch: Partial<QuickPlanDestination>) =>
    setDestinations(prev => prev.map(d => d.tempId === tempId ? { ...d, ...patch } : d));

  const addDest = () =>
    setDestinations(prev => [...prev, makeEmptyDestination()]);

  const removeDest = (tempId: string) =>
    setDestinations(prev => prev.filter(d => d.tempId !== tempId));

  const moveDest = (tempId: string, dir: -1 | 1) =>
    setDestinations(prev => {
      const idx = prev.findIndex(d => d.tempId === tempId);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });

  const validDestinations = destinations.filter(d => d.city.trim().length > 0 && d.nights > 0);
  const canSubmit =
    validDestinations.length >= 2 &&
    !!startDate &&
    !isGenerating;

  const handleSubmit = () => {
    const totalNights = validDestinations.reduce((a, d) => a + d.nights, 0);
    const resolvedTitle = title.trim() ||
      `${validDestinations.map(d => d.city).join(' → ')} · ${totalNights} nights`;

    onGenerate({
      title: resolvedTitle,
      start_date: startDate,
      travel_style: travelStyle,
      budget_level: budgetLevel,
      group_type: groupType,
      interests,
      destinations: validDestinations,
      allowed_modes: allowedModes,
      default_mode: defaultMode,
    });
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-6 md:py-10">

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Plan your trip
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            List your destinations and how many nights in each. We'll build the route, pick lodging, sights, and restaurants for each stop, and suggest detours along the way.
          </p>
        </div>

        {/* Title + start date */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Trip name <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Adriatic Coast 2026"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Start date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Destinations */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Destinations</h2>
            <span className="text-xs text-gray-400">
              {validDestinations.length} stop{validDestinations.length !== 1 ? 's' : ''} ·{' '}
              {validDestinations.reduce((a, d) => a + d.nights, 0)} night{validDestinations.reduce((a, d) => a + d.nights, 0) !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="space-y-2">
            {destinations.map((d, idx) => (
              <div key={d.tempId} className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <LocationField
                  value={{ city: d.city, country: d.country, lat: d.lat, lon: d.lon }}
                  onChange={loc => updateDest(d.tempId, loc)}
                  placeholder="City, Country"
                />
                <div className="flex items-center gap-1 flex-shrink-0 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1">
                  <button type="button"
                    onClick={() => updateDest(d.tempId, { nights: Math.max(0, d.nights - 1) })}
                    className="w-6 h-6 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center">
                    −
                  </button>
                  <span className="text-sm font-bold text-gray-900 dark:text-white w-8 text-center">
                    {d.nights}n
                  </span>
                  <button type="button"
                    onClick={() => updateDest(d.tempId, { nights: d.nights + 1 })}
                    className="w-6 h-6 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center">
                    +
                  </button>
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button type="button" onClick={() => moveDest(d.tempId, -1)} disabled={idx === 0}
                    className="w-5 h-5 text-xs text-gray-400 hover:text-teal-600 disabled:opacity-30">▲</button>
                  <button type="button" onClick={() => moveDest(d.tempId, 1)} disabled={idx === destinations.length - 1}
                    className="w-5 h-5 text-xs text-gray-400 hover:text-teal-600 disabled:opacity-30">▼</button>
                </div>
                {destinations.length > 1 && (
                  <button type="button" onClick={() => removeDest(d.tempId)}
                    className="p-1 text-gray-400 hover:text-red-500 flex-shrink-0">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button type="button" onClick={addDest}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-teal-400 hover:text-teal-600 transition-colors">
            <PlusIcon className="w-4 h-4" /> Add destination
          </button>
        </div>

        {/* Travel style */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Style &amp; preferences</h2>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Select label="Pace" value={travelStyle} set={setTravelStyle} opts={TRAVEL_STYLES as unknown as string[]} />
            <Select label="Budget" value={budgetLevel} set={setBudgetLevel} opts={BUDGET_LEVELS as unknown as string[]} />
            <Select label="Group" value={groupType} set={setGroupType} opts={GROUP_TYPES as unknown as string[]} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Interests (sightseeing focus)</label>
            <div className="flex flex-wrap gap-1.5">
              {INTERESTS.map(i => (
                <button key={i} type="button" onClick={() => toggleInterest(i)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    interests.includes(i)
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                  }`}>
                  {i}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Transit modes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">How you'll travel between stops</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Pick all modes you're open to. Each leg defaults to <strong className="text-gray-700 dark:text-gray-200">{defaultMode}</strong> — you can change any leg after generating.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {TRANSIT_MODE_OPTIONS.map(m => (
              <button key={m.value} type="button" onClick={() => toggleMode(m.value)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  allowedModes.includes(m.value)
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                }`}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Default mode</label>
            <select value={defaultMode} onChange={e => setDefaultMode(e.target.value as TransitMode)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500">
              {allowedModes.map(m => (
                <option key={m} value={m}>
                  {TRANSIT_MODE_OPTIONS.find(x => x.value === m)?.label || m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {errorMessage}
          </div>
        )}

        {progressMessage && (
          <div className="mb-4 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg text-sm text-teal-700 dark:text-teal-300 flex items-center gap-2">
            <ArrowPathIcon className="w-4 h-4 animate-spin flex-shrink-0" />
            {progressMessage}
          </div>
        )}

        <button type="button" onClick={handleSubmit} disabled={!canSubmit}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {isGenerating ? (
            <><ArrowPathIcon className="w-5 h-5 animate-spin" /> Generating…</>
          ) : (
            <><SparklesIcon className="w-5 h-5" /> Generate itinerary <ArrowRightIcon className="w-4 h-4" /></>
          )}
        </button>

        {onSwitchToAdvanced && !isGenerating && (
          <button type="button" onClick={onSwitchToAdvanced}
            className="w-full mt-2 text-xs text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400">
            Or use the advanced editor →
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable form sub-component
// ─────────────────────────────────────────────────────────────────────────────

const Select: React.FC<{
  label: string; value: string; set: (v: string) => void; opts: string[];
}> = ({ label, value, set, opts }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
    <select value={value} onChange={e => set(e.target.value)}
      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500">
      {opts.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
    </select>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// StayExplorePanel — slide-in side panel for a selected stay in explore mode
// ─────────────────────────────────────────────────────────────────────────────

type StayTab = 'sights' | 'lodging' | 'restaurants' | 'tips';

function formatDT(isoStr: string): string {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return isoStr; }
}

export interface StayExplorePanelProps {
  stay: Stay;
  stayIndex: number;
  colorHex: string;
  onClose: () => void;
  onEnrich?: () => void;
  isEnriching?: boolean;
  onFocusActivity?: (act: ItinActivity) => void;
}

export const StayExplorePanel: React.FC<StayExplorePanelProps> = ({
  stay, stayIndex, colorHex, onClose, onEnrich, isEnriching, onFocusActivity,
}) => {
  const hasSights = stay.activities.length > 0;
  const hasLodging = (stay.lodging?.recommendations?.length ?? 0) > 0;
  const hasRestaurants = stay.dining_recommendations.length > 0;
  const hasTips = (stay.travel_tips?.length ?? 0) > 0;

  const initialTab: StayTab = hasSights ? 'sights'
    : hasLodging ? 'lodging'
    : hasRestaurants ? 'restaurants'
    : 'tips';
  const [tab, setTab] = useState<StayTab>(initialTab);

  // Group activities by date for nicer layout
  const activitiesByDate = React.useMemo(() => {
    const m = new Map<string, ItinActivity[]>();
    stay.activities.forEach(a => {
      const k = a.date || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(a);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [stay.activities]);

  return (
    <div className="absolute top-0 right-0 h-full w-full md:w-[420px] bg-white dark:bg-gray-800 shadow-2xl z-[600] flex flex-col border-l border-gray-200 dark:border-gray-700">

      <div className="flex items-start gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
          style={{ backgroundColor: colorHex }}>
          {stayIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white truncate">
            {stay.location.city}{stay.location.country ? `, ${stay.location.country}` : ''}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {stay.check_in ? formatDT(stay.check_in) : '—'} → {stay.check_out ? formatDT(stay.check_out) : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {stay.nights} night{stay.nights !== 1 ? 's' : ''}
            {stay.activities.length > 0 && ` · ${stay.activities.length} activities`}
          </p>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 text-xs font-medium flex-shrink-0">
        {([
          ['sights',      `🗺 Sights (${stay.activities.length})`],
          ['lodging',     `🏨 Lodging (${stay.lodging?.recommendations?.length ?? 0})`],
          ['restaurants', `🍽 Eat (${stay.dining_recommendations.length})`],
          ['tips',        `💡 Tips${hasTips ? ` (${stay.travel_tips!.length})` : ''}`],
        ] as [StayTab, string][]).map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 px-2 py-2.5 border-b-2 transition-colors ${
              tab === k
                ? 'border-teal-600 text-teal-700 dark:text-teal-300 bg-teal-50/40 dark:bg-teal-900/10'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">

        {/* Empty / unenriched */}
        {!stay.enriched && (
          <div className="p-6 text-center">
            <SparklesIcon className="w-10 h-10 text-teal-500 mx-auto mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Nothing here yet. Generate recommendations for this stop.
            </p>
            {onEnrich && (
              <button onClick={onEnrich} disabled={isEnriching}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-60">
                {isEnriching ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
                {isEnriching ? 'Generating…' : 'Generate'}
              </button>
            )}
          </div>
        )}

        {stay.enriched && tab === 'sights' && (
          <div className="p-3 space-y-3">
            {activitiesByDate.length === 0 && (
              <p className="text-xs text-gray-400 italic p-3">No sights suggested.</p>
            )}
            {activitiesByDate.map(([date, acts]) => (
              <div key={date || 'misc'}>
                {date && (
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                    {new Date(date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                )}
                <div className="space-y-1.5">
                  {acts.map(a => (
                    <div key={a.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 hover:border-teal-400 dark:hover:border-teal-500 transition-colors cursor-pointer"
                      onClick={() => onFocusActivity?.(a)}>
                      <div className="flex items-start justify-between gap-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium capitalize ${
                              a.time_slot === 'morning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                              a.time_slot === 'afternoon' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            }`}>{a.time_slot}</span>
                            <span className="text-[11px] text-gray-400">{a.duration_hours}h</span>
                            {(a.estimated_cost_usd ?? 0) > 0 && (
                              <span className="text-[11px] text-gray-400">${a.estimated_cost_usd}</span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{a.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{a.description}</p>
                        </div>
                        {a.url && (
                          <a href={a.url} target="_blank" rel="noopener noreferrer"
                             onClick={e => e.stopPropagation()}
                             className="flex-shrink-0 text-blue-400 hover:text-blue-600 p-0.5">
                            <LinkIcon className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {stay.enriched && tab === 'lodging' && (
          <div className="p-3 space-y-2">
            {(!stay.lodging?.recommendations || stay.lodging.recommendations.length === 0) && (
              <p className="text-xs text-gray-400 italic p-3">No lodging recommendations.</p>
            )}
            {stay.lodging?.recommendations?.map((r: LodgingRec, i: number) => (
              <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{r.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{r.type} · {r.area}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    r.price_level === 'luxury' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                    r.price_level === 'budget' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}>{r.price_level}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{r.description}</p>
                {r.search_url && (
                  <a href={r.search_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline mt-1.5">
                    <LinkIcon className="w-3 h-3" /> Search Booking.com
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {stay.enriched && tab === 'restaurants' && (
          <div className="p-3 space-y-2">
            {stay.dining_recommendations.length === 0 && (
              <p className="text-xs text-gray-400 italic p-3">No restaurant recommendations.</p>
            )}
            {stay.dining_recommendations.map((r: DiningRec, i: number) => (
              <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{r.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{r.cuisine} · {r.area}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    r.price_level === 'luxury' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                    r.price_level === 'budget' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}>{r.price_level}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{r.description}</p>
                {r.url && (
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline mt-1.5">
                    <LinkIcon className="w-3 h-3" /> More info
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {stay.enriched && tab === 'tips' && (
          <div className="p-3">
            {hasTips ? (
              <ul className="space-y-2">
                {stay.travel_tips!.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-200">
                    <span className="text-teal-500 flex-shrink-0 mt-0.5">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-400 italic">No local tips.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TransitExplorePanel — side panel for a selected transit leg
// ─────────────────────────────────────────────────────────────────────────────

const TRANSIT_MODE_LABELS: Record<TransitMode, { label: string; icon: string }> = {
  drive: { label: 'Drive', icon: '🚗' },
  fly:   { label: 'Fly',   icon: '✈️' },
  train: { label: 'Train', icon: '🚆' },
  bus:   { label: 'Bus',   icon: '🚌' },
  ferry: { label: 'Ferry', icon: '⛴️' },
};

function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export interface TransitExplorePanelProps {
  transit: TransitSegment;
  allowedModes: TransitMode[];
  onClose: () => void;
  onChangeMode: (mode: TransitMode) => void;
  onLoadRouteSights: () => void;
  isRecalculating?: boolean;
  isLoadingRouteSights?: boolean;
  onFocusRouteSight?: (sight: RouteSight) => void;
}

export const TransitExplorePanel: React.FC<TransitExplorePanelProps> = ({
  transit, allowedModes, onClose, onChangeMode, onLoadRouteSights,
  isRecalculating, isLoadingRouteSights, onFocusRouteSight,
}) => {
  const modeInfo = TRANSIT_MODE_LABELS[transit.mode];
  const canLoadRouteSights = ['drive', 'train', 'bus', 'ferry'].includes(transit.mode);

  return (
    <div className="absolute top-0 right-0 h-full w-full md:w-[420px] bg-white dark:bg-gray-800 shadow-2xl z-[600] flex flex-col border-l border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-lg flex-shrink-0">
          {modeInfo.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white truncate">
            {transit.from_location.city || '?'} → {transit.to_location.city || '?'}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {modeInfo.label}
            {transit.duration_minutes ? ` · ${formatDuration(transit.duration_minutes)}` : ''}
            {transit.distance_km ? ` · ${Math.round(transit.distance_km)} km` : ''}
          </p>
          {transit.departure_time && (
            <p className="text-xs text-gray-400 mt-0.5">
              {formatDT(transit.departure_time)}
              {transit.arrival_time && ` → ${formatDT(transit.arrival_time)}`}
            </p>
          )}
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Transit mode</p>
          <div className="flex flex-wrap gap-1.5">
            {allowedModes.map(m => (
              <button key={m} onClick={() => onChangeMode(m)} disabled={isRecalculating}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-60 ${
                  transit.mode === m
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                }`}>
                {TRANSIT_MODE_LABELS[m].icon} {TRANSIT_MODE_LABELS[m].label}
              </button>
            ))}
          </div>
          {isRecalculating && (
            <p className="text-xs text-teal-600 mt-1.5 flex items-center gap-1">
              <ArrowPathIcon className="w-3 h-3 animate-spin" /> Recalculating…
            </p>
          )}
        </div>

        {transit.notes && (
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2.5">
            <p className="text-xs text-gray-500 italic">{transit.notes}</p>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Along the way</p>
            {canLoadRouteSights && (
              <button onClick={onLoadRouteSights} disabled={isLoadingRouteSights}
                className="text-xs text-teal-600 hover:text-teal-700 disabled:opacity-60 flex items-center gap-1">
                {isLoadingRouteSights
                  ? <><ArrowPathIcon className="w-3 h-3 animate-spin" /> Loading…</>
                  : transit.route_sights_loaded ? 'Refresh' : 'Suggest stops'}
              </button>
            )}
          </div>

          {!canLoadRouteSights && (
            <p className="text-xs text-gray-400 italic">
              No along-route sights for flights. Try changing mode to drive, train, bus, or ferry.
            </p>
          )}

          {canLoadRouteSights && (!transit.route_sights || transit.route_sights.length === 0) && transit.route_sights_loaded && (
            <p className="text-xs text-gray-400 italic">No notable stops suggested along this route.</p>
          )}

          {canLoadRouteSights && (!transit.route_sights || transit.route_sights.length === 0) && !transit.route_sights_loaded && !isLoadingRouteSights && (
            <p className="text-xs text-gray-400 italic">Click "Suggest stops" to discover sights on the way.</p>
          )}

          <div className="space-y-1.5 mt-2">
            {(transit.route_sights || []).map(rs => (
              <div key={rs.id || rs.name}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 hover:border-amber-400 transition-colors cursor-pointer"
                onClick={() => onFocusRouteSight?.(rs)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{rs.name}</p>
                    {rs.why_worth_it && (
                      <p className="text-xs italic text-amber-600 dark:text-amber-400 mt-0.5">{rs.why_worth_it}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{rs.description}</p>
                    <div className="flex gap-3 mt-1 text-[11px] text-gray-400">
                      {rs.detour_minutes !== undefined && <span>+{rs.detour_minutes}m detour</span>}
                      {rs.time_needed_hours !== undefined && <span>{rs.time_needed_hours}h stay</span>}
                    </div>
                  </div>
                  {rs.url && (
                    <a href={rs.url} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="flex-shrink-0 text-blue-400 hover:text-blue-600 p-0.5">
                      <LinkIcon className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ExploreBottomStrip — horizontal day-by-day timeline strip
// ─────────────────────────────────────────────────────────────────────────────

export interface ExploreBottomStripProps {
  segments: (Stay | TransitSegment)[];
  selectedStayId: string | null;
  selectedTransitId: string | null;
  onSelectStay: (id: string) => void;
  onSelectTransit: (id: string) => void;
  stayColor: (idx: number) => string;
}

function fmtDayShort(isoDate: string): string {
  if (!isoDate) return '';
  try {
    return new Date(isoDate.length === 10 ? isoDate + 'T12:00' : isoDate)
      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function fmtTime(isoStr: string): string {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
}

export const ExploreBottomStrip: React.FC<ExploreBottomStripProps> = ({
  segments, selectedStayId, selectedTransitId,
  onSelectStay, onSelectTransit, stayColor,
}) => {
  // Build ordered blocks from segments
  const blocks: Array<
    | { kind: 'stay'; stay: Stay; stayIdx: number }
    | { kind: 'transit'; transit: TransitSegment }
  > = [];
  let stayIdx = 0;
  segments.forEach(s => {
    if (s.type === 'stay') {
      blocks.push({ kind: 'stay', stay: s as Stay, stayIdx });
      stayIdx += 1;
    } else {
      blocks.push({ kind: 'transit', transit: s as TransitSegment });
    }
  });

  if (blocks.length === 0) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-t border-gray-200 dark:border-gray-700 overflow-x-auto z-[500]">
      <div className="flex items-stretch gap-1.5 px-3 py-2 min-w-max">
        {blocks.map((b, i) => {
          if (b.kind === 'stay') {
            const s = b.stay;
            const color = stayColor(b.stayIdx);
            const isSelected = s.id === selectedStayId;
            return (
              <button key={s.id} onClick={() => onSelectStay(s.id)}
                className={`flex-shrink-0 rounded-lg px-2.5 py-1.5 text-left transition-all border-2 min-w-[130px] ${
                  isSelected
                    ? 'border-teal-500 shadow-md'
                    : 'border-transparent bg-gray-50 dark:bg-gray-700/40 hover:bg-white dark:hover:bg-gray-700 hover:border-gray-300'
                }`}
                style={isSelected ? { backgroundColor: color + '15' } : undefined}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                    style={{ backgroundColor: color }}>{b.stayIdx + 1}</span>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                    {s.location.city || 'Stop'}
                  </p>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {fmtDayShort(s.check_in?.slice(0, 10) || '')}
                  {s.nights > 0 && ` · ${s.nights}n`}
                </p>
                {s.check_in && s.check_out && (
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Check-in {fmtTime(s.check_in)}
                  </p>
                )}
              </button>
            );
          }
          const t = b.transit;
          const isSelected = t.id === selectedTransitId;
          const modeInfo = TRANSIT_MODE_LABELS[t.mode];
          return (
            <button key={t.id} onClick={() => onSelectTransit(t.id)}
              className={`flex-shrink-0 flex flex-col items-center justify-center rounded-lg px-2 py-1.5 text-center transition-colors border-2 min-w-[70px] ${
                isSelected
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-transparent bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              <span className="text-base">{modeInfo.icon}</span>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mt-0.5">
                {t.duration_minutes ? formatDuration(t.duration_minutes) : modeInfo.label}
              </span>
              {t.route_sights && t.route_sights.length > 0 && (
                <span className="text-[9px] text-amber-600 mt-0.5">+{t.route_sights.length} stops</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
